import React, { useState, useEffect } from 'react';
import { Droplet, Plus, Minus } from "lucide-react";

const HydrationReminder = ({ userData }) => {
  const [waterIntake, setWaterIntake] = useState(0);
  const [waterGoal, setWaterGoal] = useState(8); // Default 8 glasses
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState({ title: '', description: '' });
  const glassSize = 250; // ml per glass

  // Get user's recommended water intake based on weight and activity level
  useEffect(() => {
    if (userData?.physicalData) {
      // Adjust water intake goal based on weight and activity level
      const weight = userData.physicalData.useMetric 
        ? userData.physicalData.weightKg 
        : userData.physicalData.weightLbs * 0.453592; // Convert lbs to kg

      const activityMultiplier = 
        userData.activityLevel === 'high' ? 1.2 :
        userData.activityLevel === 'medium' ? 1 : 0.8;
      
      // Basic formula: weight in kg * 0.033 liters of water per day
      const recommendedLiters = weight * 0.033 * activityMultiplier;
      const recommendedGlasses = Math.round(recommendedLiters * 1000 / glassSize);
      
      setWaterGoal(recommendedGlasses);
    }
  }, [userData]);

  // Custom toast implementation
  const showCustomToast = (title, description, duration = 3000) => {
    setToastMessage({ title, description });
    setShowToast(true);
    setTimeout(() => setShowToast(false), duration);
  };

  // Reminder logic - check every hour
  useEffect(() => {
    const reminderInterval = setInterval(() => {
      const progress = (waterIntake / waterGoal) * 100;
      const now = new Date();
      const currentHour = now.getHours();
      
      // Only show reminders during waking hours (8am to 8pm)
      if (currentHour >= 8 && currentHour <= 20 && progress < 75) {
        showCustomToast(
          "Hydration Reminder", 
          `Don't forget to drink water! You're at ${Math.floor(progress)}% of your daily goal.`,
          5000
        );
      }
    }, 60 * 60 * 1000); // Check every hour
    
    return () => clearInterval(reminderInterval);
  }, [waterIntake, waterGoal]);

  const addWater = () => {
    setWaterIntake(prev => Math.min(prev + 1, waterGoal));
    if (waterIntake + 1 >= waterGoal) {
      showCustomToast(
        "Hydration Goal Met!",
        "Great job staying hydrated today!",
        3000
      );
    }
  };

  const removeWater = () => {
    setWaterIntake(prev => Math.max(prev - 1, 0));
  };

  const progress = (waterIntake / waterGoal) * 100;

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
      <div className="pb-2">
        <h3 className="flex items-center text-blue-600 text-lg font-semibold">
          <Droplet className="mr-2" />
          Hydration Tracker
        </h3>
      </div>
      <div className="mt-4">
        <div className="flex flex-col space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">
              {waterIntake} of {waterGoal} glasses
            </span>
            <span className="text-sm font-medium">
              {Math.round(progress)}%
            </span>
          </div>
          
          {/* Custom progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-500">
              {waterIntake * glassSize}ml / {waterGoal * glassSize}ml
            </div>
            <div className="flex space-x-2">
              <button 
                className={`p-2 rounded border border-gray-300 ${waterIntake === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                onClick={removeWater}
                disabled={waterIntake === 0}
              >
                <Minus className="h-4 w-4" />
              </button>
              <button 
                className={`p-2 rounded bg-blue-600 text-white ${waterIntake === waterGoal ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
                onClick={addWater}
                disabled={waterIntake === waterGoal}
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          <p className="text-xs text-gray-500 mt-2">
            Recommended water intake based on your weight and activity level.
          </p>
        </div>
      </div>

      {/* Simple toast implementation */}
      {showToast && (
        <div className="fixed bottom-4 right-4 bg-white border border-gray-200 rounded-lg shadow-lg p-4 max-w-xs z-50 animate-fade-in">
          <div className="font-medium">{toastMessage.title}</div>
          <div className="text-sm text-gray-500">{toastMessage.description}</div>
        </div>
      )}
    </div>
  );
};

export default HydrationReminder;